import { type NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';

import {
  type AdvancedOrderType,
  type CheckoutMode,
  CheckoutModes,
  DEFAULT_CHECKOUT_MODE,
  LEGACY_LIMIT_QUERY_PARAM,
  parseCheckoutMode,
} from '@/constants/advancedOrder';

import { navigateTradeRoute } from '@/hooks/transactionModule/tradeRouteNavigation';

import { routes } from '@/utils/routes';
import { resolveTradeRoute, type TradeRouteState } from '@/utils/tradeRoutes';
import { type TradeType, tradeTypeFromPathname } from '@/utils/url';

export {
  CHECKOUT_MODE_PER_QUERY_PARAM,
  type CheckoutMode,
  type CheckoutModeQueryParam,
  CheckoutModes,
  isAdvancedCheckoutQuery,
  parseCheckoutMode,
} from '@/constants/advancedOrder';

type UseCheckoutModeState = [CheckoutMode, (newCheckoutMode: CheckoutMode) => void];

/** The trade type named by the homepage or trade route path; undefined on query-based pages. */
export function routeTradeType({ pathname }: Pick<NextRouter, 'pathname'>): TradeType | undefined {
  if (pathname === routes.INDEX) return 'swap';
  return tradeTypeFromPathname(pathname);
}

/**
 * Navigates the homepage or a trade route to `tradeType`, keeping the pair and chart token.
 * Replaces the history entry so Back skips mode changes.
 */
export function navigateToTradeType(
  router: NextRouter,
  tradeType: TradeType,
  state = resolveTradeRoute(router.pathname, router.query),
) {
  if (!state || state.tradeType === tradeType) return;
  navigateTradeRoute(router, 'replace', { ...state, tradeType });
}

const routeCheckoutMode = ({
  tradeType,
  sellTokenChainId,
  buyTokenChainId,
}: TradeRouteState): CheckoutMode => {
  if (tradeType !== 'swap') return CheckoutModes.advanced;
  // A swap route has no mode segment for cross-chain; the pair's networks decide it.
  return sellTokenChainId && buyTokenChainId && sellTokenChainId !== buyTokenChainId
    ? CheckoutModes.crossChain
    : CheckoutModes.market;
};

/**
 * Hook to get and set the checkout mode. Trade routes derive it from the path
 * and pair; other pages read the `mode` query.
 */
export function useCheckoutMode(): UseCheckoutModeState {
  const router = useRouter();
  const { pathname, query } = router;
  const route = useMemo(() => resolveTradeRoute(pathname, query), [pathname, query]);
  const tradeType = route?.tradeType;

  const checkoutMode = route ? routeCheckoutMode(route) : parseCheckoutMode(query.mode);

  useEffect(() => {
    if (tradeType !== undefined || router.query.mode !== LEGACY_LIMIT_QUERY_PARAM) return;

    const query = { ...router.query, mode: CheckoutModes.advanced };
    router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
  }, [router, tradeType]);

  const setCheckoutMode = useCallback(
    (newCheckoutMode: CheckoutMode) => {
      if (route) {
        const keepsAdvanced =
          newCheckoutMode === CheckoutModes.advanced && route.tradeType !== 'swap';
        navigateToTradeType(
          router,
          newCheckoutMode !== CheckoutModes.advanced
            ? 'swap'
            : keepsAdvanced
              ? route.tradeType
              : 'limit',
          route,
        );
        return;
      }

      const query: {
        mode?: CheckoutMode;
        orderType?: AdvancedOrderType | string | string[];
      } = {
        ...router.query,
        mode: newCheckoutMode,
      };

      if (newCheckoutMode === DEFAULT_CHECKOUT_MODE) delete query.mode;
      if (newCheckoutMode !== CheckoutModes.advanced) delete query.orderType;

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    },
    [router, route],
  );

  return [checkoutMode, setCheckoutMode];
}
